import { useEffect, useRef, useState } from 'react'

type UseBrowserAudioMonitorOptions = {
  audioDeviceId?: string
  isEnabled?: boolean
  isMuted?: boolean
}

/**
 * Hook to monitor audio input level using native browser APIs
 * Works without a Chime meeting session
 */
export function useBrowserAudioMonitor({
  audioDeviceId,
  isEnabled = true,
  isMuted = false
}: UseBrowserAudioMonitorOptions = {}): number {
  const [audioLevel, setAudioLevel] = useState(0)
  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isEnabled || isMuted) {
      setAudioLevel(0)
      return
    }

    let mounted = true

    const setupAudioMonitor = async () => {
      try {
        // Get audio stream
        const constraints: MediaStreamConstraints = {
          audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
          video: false
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints)

        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        streamRef.current = stream

        // Create audio context and analyser
        const audioContext = new AudioContext()
        audioContextRef.current = audioContext

        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.8
        analyserRef.current = analyser

        const source = audioContext.createMediaStreamSource(stream)
        source.connect(analyser)

        // Start monitoring
        const dataArray = new Uint8Array(analyser.frequencyBinCount)

        const updateLevel = () => {
          if (!mounted || !analyserRef.current) return

          analyserRef.current.getByteFrequencyData(dataArray)

          // Calculate average level
          let sum = 0
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i]
          }
          const average = sum / dataArray.length / 255 // Normalize to 0-1

          setAudioLevel(average)

          animationFrameRef.current = requestAnimationFrame(updateLevel)
        }

        updateLevel()
      } catch (err) {
        console.error('[useBrowserAudioMonitor] Error:', err)
        setAudioLevel(0)
      }
    }

    void setupAudioMonitor()

    return () => {
      mounted = false

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }

      if (audioContextRef.current) {
        void audioContextRef.current.close()
        audioContextRef.current = null
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }

      analyserRef.current = null
    }
  }, [audioDeviceId, isEnabled, isMuted])

  return isMuted ? 0 : audioLevel
}

